import { ENTITLEMENT } from '@heidi/workspace/contracts';
import { useQueryEntitlements } from '@heidi/workspace/data';
import { useSyncExternalStore } from 'react';
import {
  getSccAccessStatus,
  subscribeSccAccessStatus,
} from '../../../data/api/scc-access-context';

export type CodingAccessState = 'loading' | 'ready' | 'denied' | 'error';

export interface CodingDashboardPermissions {
  canViewDashboard: boolean;
  canAssignToSelf: boolean;
  canUnassignFromSelf: boolean;
  canOpenCase: boolean;
  canAssignToOtherUsers: boolean;
  isLoading: boolean;
  /** Explicit coding JWT / seat state for the dashboard gate. */
  codingAccess: CodingAccessState;
}

const CODER_PERMISSIONS: Omit<CodingDashboardPermissions, 'codingAccess'> = {
  canViewDashboard: true,
  canAssignToSelf: true,
  canUnassignFromSelf: true,
  canOpenCase: true,
  canAssignToOtherUsers: true,
  isLoading: false,
};

const NON_CODER_PERMISSIONS: Omit<CodingDashboardPermissions, 'codingAccess'> = {
  canViewDashboard: false,
  canAssignToSelf: false,
  canUnassignFromSelf: false,
  canOpenCase: false,
  canAssignToOtherUsers: false,
  isLoading: false,
};

export const useCodingDashboardPermissions = (): CodingDashboardPermissions => {
  // The dashboard gate needs loading/error state, which useIsEntitlementEnabled hides.
  // useQueryEntitlements also mints the SCC JWT when coder_seat is enabled.
  const {
    data: entitlements,
    isPending,
    isError,
  } = useQueryEntitlements([ENTITLEMENT.CODER_SEAT]);

  const sccStatus = useSyncExternalStore(
    subscribeSccAccessStatus,
    getSccAccessStatus,
    getSccAccessStatus
  );

  if (isPending) {
    return { ...NON_CODER_PERMISSIONS, isLoading: true, codingAccess: 'loading' };
  }

  if (isError || !entitlements) {
    return { ...NON_CODER_PERMISSIONS, codingAccess: 'error' };
  }

  const hasCoderSeat = entitlements.get(ENTITLEMENT.CODER_SEAT)?.enabled === true;
  if (!hasCoderSeat) {
    return { ...NON_CODER_PERMISSIONS, codingAccess: 'denied' };
  }

  if (sccStatus === 'error') {
    return { ...NON_CODER_PERMISSIONS, codingAccess: 'error' };
  }
  if (sccStatus !== 'ready') {
    // Seat enabled but mint not ready (idle/denied race) — treat as denied.
    return { ...NON_CODER_PERMISSIONS, codingAccess: 'denied' };
  }

  return { ...CODER_PERMISSIONS, codingAccess: 'ready' };
};
